/**
 * Re-provides `AuthContext` with the previewed role while a "View as
 * role" preview is active, so every role decision below it follows the
 * preview without knowing one exists. See `role-preview.ts` for the
 * design; this file is only the React half.
 *
 * Mounted in `main.tsx` directly inside `ZitadelProvider`, above
 * everything that reads a role (help, the shell, every screen).
 */
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from './AuthContext';
import { RolePreviewContext, type RolePreviewValue } from './RolePreviewContext';
import {
  applyRolePreviewToCapabilities,
  clearPreview,
  getPreviewState,
  previewChoicesFor,
  rawCapabilitiesFor,
  startPreview,
  subscribePreview,
} from './role-preview';
import { bareRole } from './roles';
import { ROLE_PRECEDENCE, type AuthContextValue, type RoleToken } from './types';
import { countEvent, getTelemetrySite } from '../observability/telemetry';
import { recordRolePreviewAudit } from '../api/v1-client';
import type { SiteId } from '../api/v1';

const EMPTY: readonly RoleToken[] = [];

function highest(roles: readonly RoleToken[]): RoleToken | null {
  for (const r of ROLE_PRECEDENCE) if (roles.includes(r)) return r;
  return null;
}

/**
 * A preview leaves two traces:
 *
 *  - a telemetry counter for every event, always;
 *  - a row in the SITE'S AUDIT TRAIL (`POST …/audit/role-preview`) for
 *    enter, exit and sign-out, on whichever site is active at that
 *    moment. It is the one write the preview's own block lets through.
 *
 * `dropped` is telemetry only: it fires when somebody ELSE is now
 * signed in on the tab (or the roles no longer allow a preview), and
 * that person's token is the wrong actor for the row -- the server
 * would rightly refuse it.
 *
 * The audit write is best-effort by design: a failure (or no active
 * site) is logged and counted, never shown, and never stops the
 * preview. The preview is look-only either way; the row is the record.
 */
function record(event: 'enter' | 'exit' | 'sign_out' | 'dropped', role: RoleToken, real: RoleToken | null): void {
  const tags = { role: bareRole(role), real_role: real ? bareRole(real) : null };
  countEvent(`role_preview.${event}`, tags);
  if (event === 'dropped') return;
  const site = getTelemetrySite();
  if (!site) {
    countEvent('role_preview.audit_skipped', { ...tags, event, reason: 'no_site' });
    return;
  }
  let pending: Promise<void>;
  try {
    pending = recordRolePreviewAudit(site as SiteId, event, bareRole(role));
  } catch (err) {
    pending = Promise.reject(err);
  }
  void pending.catch((err: unknown) => {
    console.warn('[role-preview] audit write failed', err);
    countEvent('role_preview.audit_failed', { ...tags, event });
  });
}

export function RolePreviewProvider({ children }: { children: ReactNode }) {
  const real = useContext(AuthContext);
  const qc = useQueryClient();
  const stored = useSyncExternalStore(subscribePreview, getPreviewState, getPreviewState);

  const realRoles = real?.roles ?? EMPTY;
  const userId = real?.user?.id ?? null;
  const choices = useMemo(() => previewChoicesFor(realRoles), [realRoles]);
  const realHighest = highest(realRoles);
  const settled = real ? !real.isLoading : false;
  const valid =
    stored !== null && userId !== null && stored.userId === userId && choices.includes(stored.role);
  const active: RoleToken | null = valid ? stored.role : null;

  // A stored preview that does not belong to whoever is signed in now
  // -- signed out, somebody else, or roles that no longer allow it --
  // is dropped once auth has settled. Not before: while auth is still
  // loading the stored record is what keeps writes blocked after a
  // reload.
  useEffect(() => {
    if (stored && settled && !valid) {
      record('dropped', stored.role, realHighest);
      clearPreview();
    }
  }, [stored, settled, valid, realHighest]);

  // The capability matrix in the cache was computed for whichever role
  // was active when it was fetched. Recompute it in place, from the raw
  // server answer, whenever that changes -- synchronously before paint,
  // so no frame shows the previewed role's nav with the real role's
  // buttons.
  useLayoutEffect(() => {
    const queries = qc.getQueryCache().findAll({
      predicate: (q) => q.queryKey[0] === 'v1' && q.queryKey[2] === 'capabilities',
    });
    for (const q of queries) {
      const site = q.queryKey[1];
      const raw = typeof site === 'string' ? rawCapabilitiesFor(site) : undefined;
      if (raw) qc.setQueryData(q.queryKey, applyRolePreviewToCapabilities(raw, active));
      else void qc.invalidateQueries({ queryKey: q.queryKey, exact: true });
    }
  }, [qc, active, stored]);

  const enterPreview = useCallback(
    (role: RoleToken) => {
      if (!userId || !choices.includes(role)) return;
      startPreview(role, userId);
      record('enter', role, realHighest);
    },
    [userId, choices, realHighest],
  );

  const exitPreview = useCallback(() => {
    const current = getPreviewState();
    if (!current) return;
    clearPreview();
    record('exit', current.role, realHighest);
  }, [realHighest]);

  const effective = useMemo<AuthContextValue | null>(() => {
    if (!real) return null;
    // Signing out always ends a preview, whoever calls `logout`.
    const logout = async () => {
      const current = getPreviewState();
      if (current) {
        clearPreview();
        record('sign_out', current.role, realHighest);
      }
      await real.logout();
    };
    if (!active) return { ...real, logout };
    return {
      ...real,
      roles: [active],
      user: real.user ? { ...real.user, roles: [active] } : null,
      logout,
    };
  }, [real, active, realHighest]);

  const preview = useMemo<RolePreviewValue>(
    () => ({ previewRole: active, realRoles, choices, enterPreview, exitPreview }),
    [active, realRoles, choices, enterPreview, exitPreview],
  );

  return (
    <RolePreviewContext.Provider value={preview}>
      {effective ? <AuthContext.Provider value={effective}>{children}</AuthContext.Provider> : children}
    </RolePreviewContext.Provider>
  );
}
